import { $getFromLocal, $handleError, $setToLocal, $getFromCookies, $removeFromCookies, $removeFromLocal } from '@/utils';
import { log, warn, error } from '@/utils/debugLogger';
import httpService from '@Utils/httpService';
import accountService from '@Account/service/account.service';
import authService from '@Account/service/auth.service';
import { safeReturnPath } from '@/utils/returnPath';
import { CompanyType } from '@Share/config/enums.js';
import Vue from 'vue';
import router from '@/router';

/** Every localStorage key a signed-in session writes (SET_USER) and a sign-out clears. */
const SESSION_STORAGE_KEYS = [
  'accessToken',
  'refreshToken',
  'accessTokenExpiresAt',
  'tokenExpiresIn',
  'user',
  'isImpersonateMode',
  'isMultiAccountMode',
];
// localStorage turns a missing value into these strings, e.g. SET_USER without a token.
const NON_TOKEN_STRINGS = ['undefined', 'null'];

function isUsableToken(value) {
  return typeof value === 'string' && !!value.trim() && !NON_TOKEN_STRINGS.includes(value.trim());
}

/**
 * Whether this browser holds a session, read once on load. A stored user without a usable
 * token (e.g. the fake session the old self-registration page left, token "undefined") is not
 * one: its leftovers are cleared so the visitor starts signed out.
 */
function restoreStoredSession() {
  const hasSession =
    isUsableToken($getFromLocal('accessToken', false, null)) || !!$getFromCookies('token', false, null);
  if (!hasSession && SESSION_STORAGE_KEYS.some((key) => $getFromLocal(key, false, null) != null)) {
    $removeFromLocal(SESSION_STORAGE_KEYS);
  }
  return hasSession;
}

const hasStoredSession = restoreStoredSession();

export const ACCOUNT = {
  namespaced: true,
  state: {
    user: hasStoredSession ? $getFromLocal('user', true, null) : null,
    loading: false,
    forgetLoading: false,
    resetLoading: false,
    // Check for new accessToken first, fallback to legacy cookie token
    isLogin: hasStoredSession,
    isRememberMe: false,
    isLogout: false,
    isImpersonateMode: $getFromLocal('isImpersonateMode', false, false) === "true",
    customUI: null,
    customUILoading: false,
    isMultiAccountMode: $getFromLocal('isMultiAccountMode', false, false) === "true",
    // New token-related state
    accessTokenExpiresAt: $getFromLocal('accessTokenExpiresAt', false, null),
    tokenExpiresIn: $getFromLocal('tokenExpiresIn', false, null)
  },
  getters: {
    GET_IS_AUTH(state) {
      return !!state.user && state.isLogin;
    },
    GET_USER(state) {
      return state.user;
    },
    IS_IMPERSONATE_MODE(state) {
      return state.isImpersonateMode;
    },
    IS_MULTI_ACCOUNT_MODE(state) {
      return state.isMultiAccountMode;
    },
    IS_ADMIN(state) {
      return (
        !!state.user &&
        state.user.company &&
        state.user.company.companyType == CompanyType.Management
      );
    },
    GET_COMPANY(state) {
      return state.user && state.user.company ? state.user.company : {};
    },
    GET_REMEMBER_ME(state) {
      return state.isRememberMe;
    },
    GET_USER_ROLE(state) {
      return state.user && state.user.roles?.length ? state.user.roles[0]?.name : null;
    },
    GET_IS_EULA_ACCEPTED(state) {
      return state.user ? !!state.user.hasAcceptedEula : false;
    },
    GET_CUSTOM_UI(state) {
      return state.customUI;
    },
    GET_CUSTOM_UI_LOADING(state) {
      return state.customUILoading;
    },
    GET_COMPANY_ID(state) {
      return state.user ? state.user.companyId : null;
    }
  },
  mutations: {
    /**
     * SET_USER mutation - Updated for new token-based auth
     *
     * @param {Object} payload - Login response from API
     * @param {Object} payload.user - User object
     * @param {string} payload.accessToken - Short-lived JWT access token
     * @param {string} payload.refreshToken - Long-lived refresh token
     * @param {string} payload.accessTokenExpiresAt - When the access token expires (UTC)
     * @param {number} payload.expiresIn - Token lifetime in seconds
     * @param {boolean} payload.isImpersonateMode - Whether in impersonation mode
     * @param {boolean} payload.isMultiAccountMode - Whether user has multiple accounts
     */
    SET_USER(state, { user, accessToken, refreshToken, accessTokenExpiresAt, expiresIn, isImpersonateMode, isMultiAccountMode }) {
      state.user = user;
      state.isLogin = !!user;
      state.isImpersonateMode = isImpersonateMode || false;
      state.isMultiAccountMode = isMultiAccountMode || false;
      state.accessTokenExpiresAt = accessTokenExpiresAt;
      state.tokenExpiresIn = expiresIn;

      if (state.isLogout || !user) {
        // Clear all auth data on logout
        $removeFromLocal(SESSION_STORAGE_KEYS);
        $removeFromCookies('token'); // Clear legacy cookie
        state.isLogin = false;
      } else {
        // Store new token data
        $setToLocal('accessToken', accessToken, false);
        $setToLocal('refreshToken', refreshToken, false);
        $setToLocal('accessTokenExpiresAt', accessTokenExpiresAt, false);
        $setToLocal('tokenExpiresIn', expiresIn, false);
        $setToLocal('user', user, true);
        $setToLocal('isImpersonateMode', isImpersonateMode, false);
        $setToLocal('isMultiAccountMode', isMultiAccountMode, false);
      }
      httpService.handleToken();
    },
    UPDATE_USER_BASIC(state, payload) {
      const newUser = Object.assign({}, state.user, payload);
      state.user = newUser;
      $setToLocal('user', newUser, true);
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_FORGET_LOADING(state, value) {
      state.forgetLoading = value;
    },
    SET_RESET_LOADING(state, value) {
      state.resetLoading = value;
    },
    SET_REMEMBER_ME(state, value) {
      state.isRememberMe = value;
    },
    SET_EULA_ACCEPTED(state) {
      if (state.user) {
        Vue.set(state.user, 'hasAcceptedEula', true);
        $setToLocal('user', state.user, true);
      }
    },
    SET_CUSTOM_UI(state, customUI) {
      state.customUI = customUI;
    },
    SET_CUSTOM_UI_LOADING(state, loading) {
      state.customUILoading = loading;
    }
  },
  actions: {
    /**
     * LOGIN action - Uses new Auth API with refresh token support
     *
     * The API returns:
     * - user: User object with roles, permissions, company
     * - accessToken: Short-lived JWT (15 min)
     * - refreshToken: Long-lived refresh token stored in localStorage
     * - accessTokenExpiresAt: UTC expiration timestamp
     * - expiresIn: Lifetime in seconds
     */
    LOGIN({ commit }, data) {
      commit('SET_LOADING', true);
      data = structuredClone(data);
      authService
        .login(data)
        .then((res) => {
          commit('SET_USER', res.data);
          location.href = safeReturnPath(new URLSearchParams(location.search).get('redirect')) || '/';
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    },
    /**
     * REFRESH_CURRENT_USER action - Re-fetches the authenticated user's profile
     * from the server and merges it into the cached state.
     *
     * Why this exists: the login response carries a bare (unsigned) avatarUrl
     * because the BlobUrlSigningFilter bails when no company context is available
     * yet (the JWT is being issued in that same response, so there are no claims
     * to read from). Without a post-login refresh, the cached user data in
     * localStorage holds the bare URL until sign-out, which makes the nav chip
     * and Profile Settings page show a placeholder avatar.
     *
     * Dispatching this on App mount after authentication has been established
     * pulls a fresh UserVM through /api/User/CurrentUser — where the filter now
     * sees the JWT, has company context, and returns a signed URL.
     */
    REFRESH_CURRENT_USER({ commit, state }) {
      if (!state.user || !state.isLogin) {
        return Promise.resolve();
      }
      log('ACCOUNT/REFRESH_CURRENT_USER: fetching fresh current user data');
      return accountService
        .getCurrentUser()
        .then((response) => {
          commit('UPDATE_USER_BASIC', response.data);
          log('ACCOUNT/REFRESH_CURRENT_USER: cached user data refreshed');
        })
        .catch((err) => {
          // Silent: if refresh fails we keep the stale data rather than breaking the app.
          warn('ACCOUNT/REFRESH_CURRENT_USER: refresh failed, keeping cached user', err);
        });
    },
    UPDATE_USER_PREFERENCES({ commit }, userPreferences) {
      return new Promise((resolve, reject) => {
        accountService
          .updateUserPreferences(userPreferences)
          .then((response) => {
            commit('UPDATE_USER_BASIC', { userPreferences });
            resolve(response);
          })
          .catch((error) => {
            $handleError(error);
            reject(error);
          });
      });
    },
    FORGET_PASSWORD({ commit }, data) {
      commit('SET_FORGET_LOADING', true);
      data = structuredClone(data);
      accountService
        .forgotPassword(data)
        .then(() => {
          Vue.prototype.$dialog.notify.success(
            'Please Check Your Email and Follow the Provided Instructions to Reset Your Password',
          );
          router.replace('/login');
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_FORGET_LOADING', false);
        });
    },
    RESET({ commit }, data) {
      commit('SET_RESET_LOADING', true);
      data = structuredClone(data);
      accountService
        .resetPassword(data)
        .then(() => {
          Vue.prototype.$dialog.notify.success('Password Reset Successfully');
          router.replace('/login');
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_RESET_LOADING', false);
        });
    },
    ACCEPT_EULA({ commit }) {
      return accountService.acceptEula({
        eulaVersion: "1.0",
        acceptedTerms: true,
      }).then(() => {
        commit('SET_EULA_ACCEPTED');
      }).catch((err) => {
        $handleError(err);
        throw err;
      });
    },
    /**
     * LOGOUT action - Revokes refresh token on server and clears local auth data
     */
    async LOGOUT({ commit, state }) {
      state.isLogout = true;

      try {
        // Call server to revoke refresh token (fails silently if already logged out)
        await authService.logout().catch(() => {});
      } finally {
        // Always clear local auth data
        commit('SET_USER', {
          user: null,
          accessToken: null,
          refreshToken: null,
          accessTokenExpiresAt: null,
          expiresIn: null,
          isImpersonateMode: false,
          isMultiAccountMode: false
        });
        state.isLogout = false;
        location.reload();
      }
    },

    /**
     * LOGOUT_ALL action - Revokes all sessions/devices for the current user
     */
    async LOGOUT_ALL({ commit, state }) {
      state.isLogout = true;

      try {
        const response = await authService.logoutAll();
        Vue.prototype.$dialog.notify.success(response.data.Message || 'Logged out from all devices');
      } catch (err) {
        $handleError(err);
      } finally {
        commit('SET_USER', {
          user: null,
          accessToken: null,
          refreshToken: null,
          accessTokenExpiresAt: null,
          expiresIn: null,
          isImpersonateMode: false,
          isMultiAccountMode: false
        });
        state.isLogout = false;
        location.reload();
      }
    },

    CHECK_CUSTOM_UI({ commit }, url) {
      // First check local storage for cached custom UI data
      const cachedCustomUI = $getFromLocal('customUI', true, null);

      // If we have cached data, show it immediately to improve UX
      if (cachedCustomUI) {
        commit('SET_CUSTOM_UI', cachedCustomUI);
        // Don't set loading to false yet, we still want to fetch fresh data
      } else {
      // Only show loading if we don't have cached data
        commit('SET_CUSTOM_UI_LOADING', true);
      }

      // Always fetch fresh data from server
      return accountService
        .checkCustomUI(url)
        .then((res) => {
          // Update with fresh data from server
          commit('SET_CUSTOM_UI', res.data);

          // Cache the fresh data for future use
          $setToLocal('customUI', res.data, true);

          return res.data;
        })
        .catch((err) => {
          // Only set to null if we don't have cached data
          if (!cachedCustomUI) {
            commit('SET_CUSTOM_UI', null);
          }
          log('No custom UI found or error occurred:', err);
          return cachedCustomUI; // Return cached data if server fails
        })
        .finally(() => {
          commit('SET_CUSTOM_UI_LOADING', false);
        });
    },
  },
};
